import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { CreditCard, Lock, ShieldCheck } from "lucide-react";
import { shippingCostFor, SHIPPING_COUNTRIES, FREE_SHIPPING_THRESHOLD } from "@/lib/shipping";

export default function Checkout() {
    const { t } = useTranslation();
    const { items, subtotal, grossSubtotal, bundleSavings, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [shipping, setShipping] = useState({
        full_name: user?.name || "",
        email: user?.email || "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "United Kingdom",
        phone: "",
    });
    const [payment, setPayment] = useState({
        card_number: "",
        expiry: "",
        cvc: "",
        brand: "VISA",
    });
    const [loyalty, setLoyalty] = useState(null);
    const [redeemPoints, setRedeemPoints] = useState(0);

    // Fetch loyalty balance for signed-in users
    useEffect(() => {
        if (user && user !== false) {
            api.get("/loyalty/balance").then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
        } else {
            setLoyalty(null);
            setRedeemPoints(0);
        }
    }, [user]);

    // Capture guest email for abandoned-cart reminders (debounced)
    useEffect(() => {
        const email = shipping.email.trim().toLowerCase();
        if (!/^\S+@\S+\.\S+$/.test(email)) return;
        const t = setTimeout(() => {
            localStorage.setItem("nexus_checkout_email", email);
            if (items.length) {
                api.post("/cart/track", {
                    email,
                    items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, dose: i.dose || null })),
                }).catch(() => {});
            }
        }, 1200);
        return () => clearTimeout(t);
    }, [shipping.email, items]);

    const shippingCost = shippingCostFor(shipping.country, subtotal);
    const tax = +(subtotal * 0.08).toFixed(2);
    const maxRedeemByPct = loyalty ? Math.floor((subtotal * loyalty.max_redemption_pct) / loyalty.point_value_gbp) : 0;
    const maxRedeem = loyalty ? Math.min(loyalty.points, maxRedeemByPct) : 0;
    const clampedRedeem = Math.max(0, Math.min(redeemPoints, maxRedeem));
    const loyaltyDiscount = loyalty ? +(clampedRedeem * loyalty.point_value_gbp).toFixed(2) : 0;
    const netSubtotal = Math.max(0, +(subtotal - loyaltyDiscount).toFixed(2));
    const total = +(netSubtotal + shippingCost + tax).toFixed(2);
    const awayFromFree = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

    const placeOrder = async (e) => {
        e.preventDefault();
        setError("");
        if (items.length === 0) {
            setError("Your cart is empty.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/checkout", {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, dose: i.dose || null })),
                shipping,
                payment,
                redeem_points: clampedRedeem,
            });
            clear();
            navigate(`/order/${data.id}`);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 text-center" data-testid="checkout-empty">
                <h1 className="font-display text-4xl font-extrabold uppercase tracking-tighter">{t("checkout.emptyTitle")}</h1>
                <p className="text-neutral-600 mt-4">{t("checkout.emptyBody")}</p>
                <Link to="/shop" className="inline-block mt-6 bg-[#0089c8] text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-3">
                    {t("checkout.browseCatalog")}
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24" data-testid="checkout-page">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("checkout.eyebrow")}</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter mb-8 sm:mb-12">{t("checkout.title")}</h1>

            <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
                <div className="lg:col-span-2 space-y-8 sm:space-y-10">
                    {/* Shipping */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 bg-[#0089c8] text-white font-bold flex items-center justify-center text-sm">1</div>
                            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t("checkout.sectionShipping")}</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                ["full_name", t("checkout.fullName"), "text", true, "md:col-span-2"],
                                ["email", t("checkout.email"), "email", true, "md:col-span-2"],
                                ["address", t("checkout.address"), "text", true, "md:col-span-2"],
                                ["city", t("checkout.city"), "text", true],
                                ["state", t("checkout.state"), "text", true],
                                ["zip", t("checkout.zip"), "text", true],
                            ].map(([key, label, type, req, span]) => (
                                <div key={key} className={span || ""}>
                                    <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{label}{req && " *"}</label>
                                    <input
                                        type={type}
                                        required={req}
                                        value={shipping[key]}
                                        onChange={(e) => setShipping({ ...shipping, [key]: e.target.value })}
                                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                        data-testid={`shipping-${key}`}
                                    />
                                </div>
                            ))}

                            {/* Country selector — drives regional shipping rate */}
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("checkout.country")} *</label>
                                <select
                                    required
                                    value={shipping.country}
                                    onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                                    className="w-full bg-[#f8f8f8] border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900 appearance-none"
                                    data-testid="shipping-country"
                                >
                                    {SHIPPING_COUNTRIES.map((c) => (
                                        <option key={c.name} value={c.name} className="bg-[#f8f8f8]">{`${c.name} · £${c.rate}`}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("checkout.phone")}</label>
                                <input
                                    type="tel"
                                    value={shipping.phone}
                                    onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="shipping-phone"
                                />
                            </div>

                            {/* Shipping policy hint */}
                            <div className="md:col-span-2 text-[11px] text-neutral-500 border border-neutral-200 rounded-lg p-3 leading-relaxed" data-testid="shipping-policy-hint">
                                <span className="text-[#0077b6] font-semibold">{t("checkout.shipPolicyLabel")}</span>{" "}
                                {t("checkout.shipPolicyBody", { threshold: FREE_SHIPPING_THRESHOLD })}
                                {awayFromFree > 0 && (
                                    <> · <span className="text-neutral-700">{t("checkout.shipAwayFromFree", { amount: awayFromFree.toFixed(2) })}</span></>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Payment */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 bg-[#0089c8] text-white font-bold flex items-center justify-center text-sm">2</div>
                            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t("checkout.sectionPayment")}</h2>
                            <div className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">
                                <Lock className="w-3 h-3" /> {t("checkout.demoMode")}
                            </div>
                        </div>
                        <div className="border border-neutral-200 p-6 space-y-4">
                            <div className="text-xs text-neutral-500 leading-relaxed">
                                {t("checkout.demoNote")}
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("checkout.cardNumber")} *</label>
                                <div className="relative">
                                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                                    <input
                                        required
                                        value={payment.card_number}
                                        onChange={(e) => setPayment({ ...payment, card_number: e.target.value })}
                                        placeholder="4242 4242 4242 4242"
                                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-10 pr-4 py-3 text-sm font-mono text-neutral-900"
                                        data-testid="card-number"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("checkout.expiry")} *</label>
                                    <input
                                        required
                                        value={payment.expiry}
                                        onChange={(e) => setPayment({ ...payment, expiry: e.target.value })}
                                        placeholder="12/28"
                                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm font-mono text-neutral-900"
                                        data-testid="card-expiry"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{t("checkout.cvc")} *</label>
                                    <input
                                        required
                                        value={payment.cvc}
                                        onChange={(e) => setPayment({ ...payment, cvc: e.target.value })}
                                        placeholder="123"
                                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm font-mono text-neutral-900"
                                        data-testid="card-cvc"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order summary */}
                <div className="lg:col-span-1">
                    <div className="border border-neutral-200 p-6 lg:sticky lg:top-28">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-4">{t("checkout.summaryEyebrow")}</div>
                        <div className="space-y-3 max-h-72 overflow-y-auto mb-6 pr-2">
                            {items.map((it) => (
                                <div key={it.variant_id || it.product_id} className="flex gap-3 text-sm" data-testid={`summary-${it.slug}${it.dose ? `-${it.dose}` : ""}`}>
                                    <div className="w-12 h-12 bg-white border border-neutral-200 overflow-hidden flex-shrink-0 rounded-lg">
                                        <img src={imgUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-semibold truncate">{it.name}</div>
                                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                                            {it.dose && (
                                                <span className="font-mono text-[#0077b6] border border-[#00aeef]/30 px-1.5 py-0.5 rounded text-[10px]">{it.dose}</span>
                                            )}
                                            <span>{t("checkout.qty", { n: it.quantity })}</span>
                                        </div>
                                    </div>
                                    <div className="font-mono text-xs">£{(it.price * it.quantity).toFixed(2)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-neutral-200 pt-4 space-y-2 text-sm">
                            {bundleSavings > 0 && (
                                <>
                                    <Row
                                        label={t("checkout.sumBundle")}
                                        value={`£${grossSubtotal.toFixed(2)}`}
                                        muted
                                    />
                                    <Row
                                        label={t("checkout.sumBundleSavings")}
                                        value={`−£${bundleSavings.toFixed(2)}`}
                                        accent
                                        testid="checkout-bundle-savings"
                                    />
                                </>
                            )}
                            <Row label={t("checkout.sumSubtotal")} value={`£${subtotal.toFixed(2)}`} />
                            {loyalty && loyalty.points > 0 && maxRedeem > 0 && (
                                <div className="pt-2 pb-1" data-testid="loyalty-redeem">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] font-semibold">
                                                {t("loyalty.redeemLabel")}
                                            </div>
                                            <div className="text-[11px] text-neutral-500">
                                                {t("loyalty.redeemHint", {
                                                    balance: loyalty.points.toLocaleString(),
                                                    max: maxRedeem.toLocaleString(),
                                                    pct: Math.round(loyalty.max_redemption_pct * 100),
                                                })}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-sm font-bold text-[#0077b6]" data-testid="loyalty-redeem-value">
                                                −£{loyaltyDiscount.toFixed(2)}
                                            </div>
                                            <div className="text-[10px] text-neutral-500">
                                                {clampedRedeem} {t("loyalty.pts")}
                                            </div>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max={maxRedeem}
                                        step="10"
                                        value={clampedRedeem}
                                        onChange={(e) => setRedeemPoints(parseInt(e.target.value, 10))}
                                        className="w-full accent-[#0089c8] cursor-pointer"
                                        data-testid="loyalty-redeem-slider"
                                    />
                                    <div className="flex gap-2 mt-2">
                                        <button
                                            type="button"
                                            onClick={() => setRedeemPoints(0)}
                                            className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 hover:text-neutral-900 border border-neutral-200 rounded px-2 py-1"
                                            data-testid="loyalty-redeem-none"
                                        >
                                            {t("loyalty.redeemNone")}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setRedeemPoints(maxRedeem)}
                                            className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] hover:text-[#005f92] border border-[#00aeef]/40 rounded px-2 py-1"
                                            data-testid="loyalty-redeem-max"
                                        >
                                            {t("loyalty.redeemMax")}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {loyaltyDiscount > 0 && (
                                <Row
                                    label={t("loyalty.rowLabel", { pts: clampedRedeem })}
                                    value={`−£${loyaltyDiscount.toFixed(2)}`}
                                    accent
                                    testid="checkout-loyalty-savings"
                                />
                            )}
                            <Row label={t("checkout.sumShipping")} value={shippingCost === 0 ? t("checkout.sumShippingFree") : `£${shippingCost.toFixed(2)}`} />
                            <Row label={t("checkout.sumTax")} value={`£${tax.toFixed(2)}`} />
                        </div>
                        <div className="border-t border-neutral-200 mt-4 pt-4 flex items-baseline justify-between">
                            <div className="text-xs uppercase tracking-[0.22em] text-neutral-600">{t("checkout.sumTotal")}</div>
                            <div className="font-display text-3xl font-extrabold text-[#0077b6]" data-testid="checkout-total">£{total.toFixed(2)}</div>
                        </div>

                        {error && (
                            <div className="mt-4 border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3" data-testid="checkout-error">{error}</div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-6 w-full bg-[#f8f8f8] hover:bg-neutral-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                            data-testid="place-order-btn"
                        >
                            {loading ? t("checkout.placing") : `${t("checkout.place")} · £${total.toFixed(2)}`}
                        </button>

                        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                            <ShieldCheck className="w-3 h-3 text-[#0077b6]" /> {t("checkout.secureNote")}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

function Row({ label, value, muted = false, accent = false, testid }) {
    return (
        <div
            className="flex items-center justify-between"
            data-testid={testid}
        >
            <div className={muted ? "text-neutral-400 line-through" : accent ? "text-[#0077b6] uppercase tracking-[0.18em] text-xs font-semibold" : "text-neutral-500"}>
                {label}
            </div>
            <div className={`font-mono ${muted ? "text-neutral-400 line-through" : accent ? "text-[#0077b6] font-bold" : "text-neutral-800"}`}>
                {value}
            </div>
        </div>
    );
}
